import { useMemo } from "react";
import { useSelector } from "react-redux";

import { DEFAULT_FONT_SIZE } from "constants/chart.constants";
import { useChartsContext } from "contexts";
import { RootState } from "store/rootReducer";
import styled from "styled-components";

import { useChartEntities } from "hooks/charts";

import { useScreenshotContext } from "components/screenshot/hooks";

import {
  ForecastCompareLineStyle,
  getForecastCompareFolders
} from "./utils/getForecastCompareFolders";

const ForecastCompareLegend = () => {
  const { apiResponse, screenshot } = useChartEntities(useChartsContext().id);
  // The response carries the schema the chart was actually built from; the request state is the
  // fallback for a response that predates the sync (or omits the block when sync is off).
  const requestedSyncSchema = useSelector(
    (state: RootState) => state.app.syncWells?.schema
  );
  const { settings } = useScreenshotContext();
  const fontSize = screenshot.visible
    ? Number(settings?.legendFontSize) || DEFAULT_FONT_SIZE
    : DEFAULT_FONT_SIZE;
  const isWrappingText = screenshot.visible && (settings?.isWrappingText ?? false);

  const syncedSchema = apiResponse?.syncStatus?.syncedSchema || requestedSyncSchema;
  const displayFolders = useMemo(
    () => getForecastCompareFolders(apiResponse?.series, syncedSchema),
    [apiResponse?.series, syncedSchema]
  );

  if (displayFolders.length === 0) {
    return null;
  }

  return (
    <SubtitleContainer>
      {displayFolders.map((folder, index) => (
        <LineIndicator
          key={`folder-${index}`}
          data-testid="forecast-compare-folder"
          fontSize={fontSize}
          isWrapping={isWrappingText}>
          {/* The indicator carries a testid so a test can assert the style each source was
              drawn with, not just the order the names came out in. */}
          {folder.lineStyle === ForecastCompareLineStyle.Dashed ? (
            <DashedLine data-testid="forecast-compare-dashed" fontSize={fontSize} />
          ) : (
            <DottedLine data-testid="forecast-compare-dotted" fontSize={fontSize} />
          )}
          <FolderName isWrapping={isWrappingText}>{folder.name}</FolderName>
        </LineIndicator>
      ))}
    </SubtitleContainer>
  );
};

export default ForecastCompareLegend;

const SubtitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 0 12px 4px 12px;
  border-top: none;
`;

const LineIndicator = styled.div<{ fontSize?: number; isWrapping?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: ${(props) => (props.fontSize ? `${props.fontSize}px` : "12px")};
  color: #000000;
  font-weight: 400;
  flex-wrap: wrap;
`;

const DottedLine = styled.div<{ fontSize?: number }>`
  width: ${(props) => (props.fontSize ? `${props.fontSize * 1.3}px` : "16px")};
  height: ${(props) =>
    props.fontSize ? `${Math.max(props.fontSize * 0.15, 2)}px` : "2px"};
  background-image: linear-gradient(
    to right,
    #000000 0%,
    #000000 50%,
    transparent 50%,
    transparent 100%
  );
  background-size: ${(props) =>
    props.fontSize
      ? `${props.fontSize * 0.27}px ${Math.max(props.fontSize * 0.15, 2)}px`
      : "4px 2px"};
  background-repeat: repeat-x;
  flex-shrink: 0;
  align-self: center;
`;

const DashedLine = styled.div<{ fontSize?: number }>`
  width: ${(props) => (props.fontSize ? `${props.fontSize * 1.3}px` : "16px")};
  height: ${(props) =>
    props.fontSize ? `${Math.max(props.fontSize * 0.15, 2)}px` : "2px"};
  background-image: linear-gradient(
    to right,
    #000000 0%,
    #000000 80%,
    transparent 80%,
    transparent 100%
  );
  background-size: ${(props) =>
    props.fontSize
      ? `${props.fontSize * 0.7}px ${Math.max(props.fontSize * 0.15, 2)}px`
      : "10px 2px"};
  background-repeat: repeat-x;
  flex-shrink: 0;
  align-self: center;
`;

const FolderName = styled.span<{ isWrapping?: boolean }>`
  flex: 1;
  min-width: 0;
  white-space: ${(props) => (props.isWrapping ? "normal" : "nowrap")};
  word-break: ${(props) => (props.isWrapping ? "break-word" : "normal")};
  overflow: ${(props) => (props.isWrapping ? "visible" : "hidden")};
  text-overflow: ${(props) => (props.isWrapping ? "clip" : "ellipsis")};
`;
